import { useState } from "react";
import { locationOptions } from "../../data/locations";
import { submitToHubspot } from "../../utils/hubspot";

const initialState = {
  firstName: "", lastName: "", email: "", phone: "",
  typeOfBusiness: "", locations: [], message: "", gdpr: false, honeypot: "",
};

export default function EnquiryForm({ pageUri, pageName, preselectedLocation }) {
  const [form, setForm] = useState({ ...initialState, locations: preselectedLocation ? [preselectedLocation] : [] });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  function handleLocationChange(e) {
    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
    setForm((prev) => ({ ...prev, locations: selected }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.honeypot) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitToHubspot({ firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone, typeOfBusiness: form.typeOfBusiness, locations: form.locations, message: form.message, pageUri, pageName });
      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly at hello@artistic-spaces.co.uk");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass = "w-full bg-transparent border border-gray-600 text-white placeholder-gray-400 px-4 py-3 text-base focus:outline-none focus:border-white transition-colors";

  if (success) {
    return (
      <section className="bg-gray-900 py-16 px-8 md:px-16 lg:px-24">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">Thanks for getting in touch!</h2>
          <p className="text-gray-400 text-base">We'll be in touch with you shortly.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gray-900 py-12 px-8 md:px-16 lg:px-24" id="enquiry-form">
      <div className="flex flex-col md:flex-row gap-12 md:gap-24 items-start">
        <div className="md:w-1/3 flex-shrink-0 pt-2">
          <h2 className="text-3xl md:text-4xl font-semibold text-white">Get in touch</h2>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input type="text" name="firstName" value={form.firstName} onChange={handleChange} required placeholder="First Name" className={inputClass} />
            <input type="text" name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Last Name" className={inputClass} />
          </div>
          <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="Email" className={inputClass} />
          <div className="flex items-center border border-gray-600 focus-within:border-white transition-colors">
            <span className="flex items-center gap-2 px-3 text-base text-gray-400 border-r border-gray-600 py-3 flex-shrink-0">🇬🇧 +44</span>
            <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="Phone number" className="flex-1 bg-transparent text-white placeholder-gray-400 px-4 py-3 text-base focus:outline-none" />
          </div>
          <input type="text" name="typeOfBusiness" value={form.typeOfBusiness} onChange={handleChange} required placeholder="Type of business" className={inputClass} />
          <div>
            <label className="block text-base text-gray-300 mb-1">Location</label>
            <p className="text-sm text-gray-500 mb-2">To select multiple locations, hold Cmd or Ctrl.</p>
            <select name="locations" multiple value={form.locations} onChange={handleLocationChange} className="w-full bg-gray-800 border border-gray-600 text-white text-base px-4 py-2 focus:outline-none focus:border-white transition-colors min-h-[120px]">
              <option value="All locations">All locations</option>
              {locationOptions.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          </div>
          <textarea name="message" value={form.message} onChange={handleChange} required rows={5} placeholder="Message" className={`${inputClass} resize-y`} />
          <div className="flex items-start gap-3">
            <input type="checkbox" name="gdpr" id="gdpr" checked={form.gdpr} onChange={handleChange} required className="mt-1 h-4 w-4 border-gray-600 bg-transparent accent-[#8a7d5a]" />
            <label htmlFor="gdpr" className="text-sm text-gray-400 leading-relaxed">
              I agree with the handling of my data in accordance with this enquiry form and the Artistic Spaces{" "}
              <a href="/privacy-policy" className="underline hover:text-white">Privacy Policy</a>
            </label>
          </div>
          <div style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
            <input type="text" name="honeypot" value={form.honeypot} onChange={handleChange} tabIndex={-1} autoComplete="off" />
          </div>
          {error && <p className="text-base text-red-400">{error}</p>}
          <button type="submit" disabled={submitting} className="self-start px-8 py-3 bg-[#8a7d5a] text-white text-sm font-semibold tracking-widest uppercase hover:bg-[#7a6d4a] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </section>
  );
}